import { memo, useMemo, type FC } from 'react';
import { Card, Flex, Typography, Skeleton } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import StatusCardItem from './status-card';
import StatusNoteItem from './status-note-item';
import styles from './styles.module.scss';

import type { TOverviewPatientProps, TStatusCard } from './types';

const { Text } = Typography;

const numberFormatter = new Intl.NumberFormat();

const OverviewPatients: FC<TOverviewPatientProps> = ({
  roleNames = [],
  title,
  variant = 'full',
  loading = false,
  statusCards = [],
  statusNotes = [],
  className,
  emptyStateText,
  valueFormatter = (value: number) => numberFormatter.format(value),
  headerAction,
  selectedStatusId = null,
  onStatusCardSelect,
}) => {
  const { t } = useTranslation();
  const resolvedTitle = title || t('homeDashboard.overviewTitle');

  const getFilteredCards = () => {
    let filtered = (statusCards ?? []).filter(Boolean) as TStatusCard[];

    const includeInactive = roleNames.includes('medicalAssistant');
    if (!includeInactive) {
      filtered = filtered.filter((card) => card.id !== 'inactive');
    }

    const criticalCard = filtered.find((card) => card.id === 'critical');
    if (criticalCard?.value === 0) {
      filtered = filtered.filter((card) => card.id !== 'critical');
    }

    return filtered;
  };

  const cards = useMemo(() => getFilteredCards(), [statusCards, roleNames]);
  const notes = statusNotes?.filter(Boolean) ?? [];

  const canShowCards = variant !== 'notes-only' && cards.length > 0;
  const canShowNotes = variant !== 'cards-only' && notes.length > 0;
  const showEmptyState = !loading && !canShowCards && !canShowNotes;

  return (
    <Card
      className={classNames(styles['overview-card'], className)}
      variant="borderless"
    >
      <Flex
        align="center"
        justify="space-between"
        className={styles['overview-header']}
        gap={12}
      >
        {resolvedTitle && (
          <Text className={styles['overview-title']}>{resolvedTitle}</Text>
        )}
        {headerAction ? (
          <div className={styles['header-meta']}>
            <div className={styles['header-action']}>{headerAction}</div>
          </div>
        ) : null}
      </Flex>

      <Skeleton active loading={loading}>
        <div className={styles['status-row']} data-variant={variant}>
          {canShowCards && (
            <div
              className={styles['status-cards']}
              data-card-count={cards.length}
            >
              {cards.map((card) => {
                const displayLabel = card.id
                  ? t(`homeDashboard.status.${card.id}`, card.label ?? card.id)
                  : card.label;

                return (
                  <StatusCardItem
                    key={card.id ?? card.label}
                    {...card}
                    label={displayLabel}
                    valueFormatter={valueFormatter}
                    isInteractive={
                      Boolean(onStatusCardSelect) &&
                      (card.id === 'critical' ||
                        card.id === 'at-risk' ||
                        card.id === 'normal')
                    }
                    isActive={selectedStatusId === card.id}
                    onSelect={onStatusCardSelect}
                  />
                );
              })}
            </div>
          )}

          {canShowNotes && (
            <div className={styles['status-notes']}>
              {notes.map((note) => (
                <StatusNoteItem
                  key={note.id ?? note.label}
                  {...note}
                  label={
                    note.id
                      ? t(`homeDashboard.notes.${note.id}`, note.label)
                      : note.label
                  }
                  valueFormatter={valueFormatter}
                />
              ))}
            </div>
          )}

          {showEmptyState && (
            <Typography.Text type="secondary">
              {emptyStateText ||
                t(
                  'homeDashboard.overviewEmpty',
                  'No patient overview data yet',
                )}
            </Typography.Text>
          )}
        </div>
      </Skeleton>
    </Card>
  );
};

export default memo(OverviewPatients);
